import { useMemo, useState } from "react";
import { ShieldCheck, CheckCircle2, Clock, Loader2, ChevronDown } from "lucide-react";

import {
  useGetLogbookResultsForApprovalQuery,
  useApproveLogbookResultsMutation,
} from "../api/logbookAssessmentDietApi";

type Tab = "pending" | "approved";

const fmtDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const resultOf = (lb: any): string => {
  const a = Array.isArray(lb?.assessments) ? lb.assessments[0] : null;
  return String(a?.result ?? "").toLowerCase();
};

/**
 * How an assessment result reads on screen.
 *
 * The diet's cut-off writes the real outcome, and a failure is one of two
 * different re-sits — six months for falling below the mark, twelve for
 * contravening. Those are separate decisions and must not both render as a
 * generic "fail". `fail` itself is an assessor's own opinion recorded before
 * the cut-off has been applied, when the re-sit is not yet decided.
 */
const RESULT_LABELS: Record<string, string> = {
  pass: "Pass",
  resit_6_months: "Re-present · 6 months",
  resit_12_months: "Resubmit · 12 months",
  fail: "Fail (awaiting cut-off)",
};

const resultLabel = (res: string) => RESULT_LABELS[res] ?? res.replace(/_/g, " ");

const RESULT_TONES: Record<string, string> = {
  pass: "bg-emerald-50 text-emerald-700 border-emerald-200",
  resit_12_months: "bg-orange-50 text-orange-700 border-orange-200",
};

const resultTone = (res: string) => RESULT_TONES[res] ?? "bg-red-50 text-red-700 border-red-200";

/**
 * The board releases a diet at a time, not a candidate at a time — a cut-off is
 * applied to a whole cycle and the results go out together, so a screen that
 * mixed several diets into one list made the actual unit of the decision
 * invisible. Rows are therefore grouped by assessment diet, with the outcome
 * breakdown on each group's header: what the board is agreeing to is the SHAPE
 * of a diet's results, and reading that off a flat list means counting rows.
 */
type DietGroup = {
  id: string;
  title: string;
  rows: any[];
  counts: Record<string, number>;
};

function groupByDiet(rows: any[]): DietGroup[] {
  const byId = new Map<string, DietGroup>();

  for (const lb of rows) {
    const diet = lb?.assessment_diet;
    const id = String(diet?.id ?? "__none__");

    if (!byId.has(id)) {
      byId.set(id, {
        id,
        title: diet?.title ?? "No assessment diet",
        rows: [],
        counts: {},
      });
    }

    const group = byId.get(id)!;
    group.rows.push(lb);

    const res = resultOf(lb) || "unknown";
    group.counts[res] = (group.counts[res] ?? 0) + 1;
  }

  // Biggest first — the diet with the most outstanding results is the one the
  // board is sitting to deal with.
  // Array.from rather than a spread: this project's TS target predates
  // downlevelIteration, so spreading a Map iterator does not compile.
  return Array.from(byId.values()).sort((a, b) => b.rows.length - a.rows.length);
}

export default function AdminLogbookResultApprovalPage() {
  const [tab, setTab] = useState<Tab>("pending");
  const { data, isLoading, isError, refetch } =
    useGetLogbookResultsForApprovalQuery(tab);
  const [approve, { isLoading: approving }] = useApproveLogbookResultsMutation();

  const rows: any[] = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data]);

  const groups = useMemo(() => groupByDiet(rows), [rows]);

  const [picked, setPicked] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const toggle = (id: string) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const toggleGroup = (group: DietGroup) => {
    const ids = group.rows.map((r) => r.id);
    const allIn = ids.every((id) => picked.includes(id));
    setPicked((p) =>
      allIn
        ? p.filter((id) => !ids.includes(id))
        : p.concat(ids.filter((id) => !p.includes(id))),
    );
  };

  const doApprove = async (ids: string[], what: string) => {
    if (!ids.length) return;
    if (
      !window.confirm(
        `Approve and release ${ids.length} result(s) — ${what}?\n\n`
          + "The member(s) will be able to view them immediately. This cannot be undone."
      )
    )
      return;
    setMsg(null);
    try {
      const res: any = await approve({ logbook_ids: ids }).unwrap();
      setMsg({ ok: true, text: res?.message || "Results approved." });
      setPicked([]);
      refetch();
    } catch (e: any) {
      setMsg({ ok: false, text: e?.data?.message || "Could not approve results." });
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
          <ShieldCheck className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-800">Logbook Result Approval</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Board review — assessed logbook results are withheld from members until approved here.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {(["pending", "approved"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => {
              setTab(t);
              setPicked([]);
            }}
            className={`px-4 py-2 text-sm rounded-lg border capitalize flex items-center gap-1.5 ${
              tab === t
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            {t === "pending" ? <Clock className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
            {t === "pending" ? "Awaiting approval" : "Approved"}
          </button>
        ))}
        <button
          onClick={() => refetch()}
          className="ml-auto px-3 py-2 text-sm rounded-lg border hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {msg && (
        <div
          className={`mb-4 text-sm p-3 rounded-lg border ${
            msg.ok
              ? "bg-emerald-50 border-emerald-100 text-emerald-700"
              : "bg-red-50 border-red-100 text-red-700"
          }`}
        >
          {msg.text}
        </div>
      )}

      {tab === "pending" && rows.length > 0 && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-500">
            {picked.length} selected across {groups.length} diet{groups.length === 1 ? "" : "s"}
          </span>
          <button
            onClick={() => doApprove(picked, "the current selection")}
            disabled={!picked.length || approving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
          >
            {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Approve selected
          </button>
        </div>
      )}

      {isLoading && (
        <div className="bg-white border rounded-xl px-4 py-10 text-center text-gray-400">Loading…</div>
      )}
      {isError && (
        <div className="bg-white border rounded-xl px-4 py-10 text-center text-red-500">Failed to load.</div>
      )}
      {!isLoading && !isError && groups.length === 0 && (
        <div className="bg-white border rounded-xl px-4 py-10 text-center text-gray-400">
          {tab === "pending" ? "No results awaiting approval." : "No approved results yet."}
        </div>
      )}

      <div className="space-y-4">
        {groups.map((group) => {
          const ids = group.rows.map((r) => r.id);
          const allIn = ids.every((id) => picked.includes(id));
          const isOpen = !collapsed[group.id];

          return (
            <div key={group.id} className="bg-white border rounded-xl overflow-hidden">
              {/* Diet header — the unit the board actually decides on. */}
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-indigo-50/40 border-b">
                {tab === "pending" && (
                  <input
                    type="checkbox"
                    checked={allIn}
                    onChange={() => toggleGroup(group)}
                    title="Select every result in this diet"
                    className="accent-indigo-600"
                  />
                )}

                <button
                  type="button"
                  onClick={() =>
                    setCollapsed((c) => ({ ...c, [group.id]: !c[group.id] }))
                  }
                  className="flex items-center gap-2 min-w-0 text-left"
                >
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "" : "-rotate-90"}`}
                  />
                  <span className="font-semibold text-gray-900 truncate">{group.title}</span>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {group.rows.length} result{group.rows.length === 1 ? "" : "s"}
                  </span>
                </button>

                {/* Outcome breakdown: what the board is agreeing to, without
                    counting rows. */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {Object.entries(group.counts).map(([res, n]) => (
                    <span
                      key={res}
                      className={`text-[11px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${resultTone(res)}`}
                    >
                      {resultLabel(res)} · {n}
                    </span>
                  ))}
                </div>

                {tab === "pending" && (
                  <button
                    onClick={() => doApprove(ids, `the whole of "${group.title}"`)}
                    disabled={approving}
                    className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-60 whitespace-nowrap"
                  >
                    {approving ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    )}
                    Approve all {group.rows.length}
                  </button>
                )}
              </div>

              {isOpen && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                      <tr>
                        {tab === "pending" && <th className="px-4 py-3 w-10" />}
                        <th className="text-left px-4 py-3 font-semibold">Candidate</th>
                        <th className="text-left px-4 py-3 font-semibold">Stage</th>
                        <th className="text-left px-4 py-3 font-semibold">Result</th>
                        <th className="text-left px-4 py-3 font-semibold">Score</th>
                        <th className="text-left px-4 py-3 font-semibold">
                          {tab === "pending" ? "Assessed" : "Approved"}
                        </th>
                        {tab === "pending" && (
                          <th className="px-4 py-3 text-right font-semibold">Action</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {group.rows.map((lb) => {
                        const res = resultOf(lb);
                        return (
                          <tr key={lb.id} className="hover:bg-gray-50">
                            {tab === "pending" && (
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={picked.includes(lb.id)}
                                  onChange={() => toggle(lb.id)}
                                  className="accent-indigo-600"
                                />
                              </td>
                            )}
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900 flex items-center gap-1.5">
                                {lb.user?.name ?? "—"}
                                {lb.manual_result ? (
                                  <span
                                    title="Typed in by an admin from a paper/off-system record"
                                    className="inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200"
                                  >
                                    Manual
                                  </span>
                                ) : null}
                              </div>
                              <div className="text-xs text-gray-400">
                                {lb.user?.member?.membership_no ?? lb.user?.email ?? ""}
                              </div>
                            </td>
                            <td className="px-4 py-3">Stage {lb.stage ?? "—"}</td>
                            <td className="px-4 py-3">
                              {res ? (
                                <span
                                  className={`text-[11px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${resultTone(res)}`}
                                >
                                  {resultLabel(res)}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="px-4 py-3 font-semibold text-gray-700">
                              {lb.total_score != null ? `${lb.total_score}/100` : "—"}
                            </td>
                            <td className="px-4 py-3 text-gray-500">
                              {tab === "pending" ? fmtDate(lb.graded_at) : fmtDate(lb.result_approved_at)}
                            </td>
                            {tab === "pending" && (
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => doApprove([lb.id], lb.user?.name ?? "this candidate")}
                                  disabled={approving}
                                  className="px-3 py-1.5 rounded-lg border text-xs font-semibold hover:bg-gray-50 disabled:opacity-60"
                                >
                                  Approve
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
